import React, { useEffect, useState } from "react";
import api from "../../../api/axios";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import SearchBar from "../../../components/SearchBar";
import Pagination from "../../../components/Pagination";
import LoadingOverlay from "../../../components/LoadingOverlay";
import { getUserRole } from "../../../utils/getUserRole";

function Actives() {
  const isOdontologyAdmin = getUserRole() === "Admin-Odontologia";
  const [actives, setActives] = useState([]);
  const navigate = useNavigate();
  const [filteredActives, setFilteredActives] = useState([]);

  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [areas, setAreas] = useState([]);
  const [areaFilter, setAreaFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  // paginación
  const itemsPerPage = 50;
  const [currentPage, setCurrentPage] = useState(0);


  useEffect(() => {
    fetchActives();
    fetchCategories();
    fetchLocations();
    fetchAreas();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, categoryFilter, locationFilter, stateFilter, areaFilter]);

  const fetchActives = async () => {
    try {
      setLoading(true);
      const res = await api.get("/actives");
      setActives(res.data);
    } catch {
      toast.error("Error al cargar activos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get("/active-categories");
      setCategories(res.data);
    } catch {
      toast.error("Error al cargar categorías");
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await api.get("/active-locations");
      setLocations(res.data);
    } catch {
      toast.error("Error al cargar ubicaciones");
    }
  };

  const fetchAreas = async () => {
  try {
    const res = await api.get("/active-areas");
    console.log("Áreas:", res.data);
    setAreas(res.data);
  } catch (error) {
    console.error(error);
    toast.error("Error al cargar áreas");
  }
};

  const filtered = actives.filter((active) => {
    const matchSearch = `${active.model} ${active.serial_number} ${active.brand}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchCategory =
      categoryFilter === "all" ||
      active.category?.id === parseInt(categoryFilter);

    const matchLocation =
      locationFilter === "all" ||
      active.location?.id === parseInt(locationFilter);

    const matchState =
      stateFilter === "all" || active.state === stateFilter;

    const matchArea =
      areaFilter === "all" ||
      active.area?.id === parseInt(areaFilter);

    return matchSearch && matchCategory && matchLocation && matchState && matchArea;
  });

  const offset = currentPage * itemsPerPage;
  const currentItems = filtered.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(filtered.length / itemsPerPage);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("¿Estás seguro de eliminar este activo?");
    if (!confirmDelete) return;

    try {
      await api.delete(`/actives/${id}`);
      toast.success("Activo eliminado correctamente");

      // Actualiza lista sin volver a cargar todo
      setActives((prev) => prev.filter((a) => a.id !== id));
    } catch (error) {
      toast.error("Error al eliminar el activo");
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />

      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <h2 className="text-3xl font-semibold text-gray-800">Activos</h2>

        <div className="flex gap-3 items-center">
          <SearchBar
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Estado */}
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="border rounded p-2"
          >
            <option value="all">Estado</option>
            <option value="En Uso">En Uso</option>
            <option value="Disponible">Disponible</option>
            <option value="Reparación">Reparación</option>
          </select>


          {/* Categoría */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border rounded p-2"
          >
            <option value="all">Categoría</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          {/* Ubicación / Consultorio */}
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="border rounded p-2"
          >
            <option value="all">Ubicación</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>

          <Link
            to="/actives/create"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition whitespace-nowrap"
          >
            <Plus size={18} />
            Nuevo Activo
          </Link>
        </div>
      </div>

      
        {isOdontologyAdmin ? (
          <div className="mb-6">
            <span className="inline-flex rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 font-medium text-blue-700">
              Área: Odontología
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setAreaFilter("all")}
              className={`px-4 py-2 rounded-lg border ${areaFilter === "all"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700"
                }`}
            >
              Todos
            </button>

            {areas.map((area) => (
              <button
                key={area.id}
                onClick={() => setAreaFilter(area.id.toString())}
                className={`px-4 py-2 rounded-lg border ${areaFilter === area.id.toString()
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700"
                  }`}
              >
                {area.name}
              </button>
            ))}
          </div>
        )}



      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">
              <th className="px-6 py-3">Modelo</th>
              <th className="px-6 py-3">N° Serie</th>
              <th className="px-6 py-3">Marca</th>
              {/* <th className="px-6 py-3">Cantidad</th> */}
              <th className="px-6 py-3">Estado</th>
              <th className="px-6 py-3">Categoría</th>
              <th className="px-6 py-3">Ubicación</th>
              <th className="px-6 py-3 text-center w-28">Acciones</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-6 text-center text-gray-400 italic">
                  No hay registros.
                </td>
              </tr>
            ) : (
              currentItems.map((active, index) => (
                <tr
                  key={active.id}
                  className={`border-t ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                >
                  <td className="px-6 py-4">{active.model}</td>
                  <td className="px-6 py-4">{active.serial_number || "NR"}</td>
                  <td className="px-6 py-4">{active.brand}</td>
                  {/* <td className="px-6 py-4">{active.quantity}</td> */}
                  <td className="px-6 py-4">{active.state}</td>
                  <td className="px-6 py-4">
                    {active.category?.name || "Sin categoría"}
                  </td>
                  <td className="px-6 py-4">
                    {active.location?.name || "Sin ubicación"}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      {/* Editar */}
                      <Link
                        to={`/actives/edit/${active.id}`}
                        className="bg-yellow-400 hover:bg-yellow-500 text-white p-2 rounded-lg shadow transition-all duration-300"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </Link>

                      {/* Eliminar */}
                      <button
                        onClick={() => handleDelete(active.id)}
                        className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg shadow transition-all duration-300"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            currentItemsCount={currentItems.length}
            totalItemsCount={filtered.length}
          />
        )}
      </div>
    </div>
  );
}

export default Actives;
